import { EditOutlined, LinkOutlined } from "@ant-design/icons";
import { ICellRendererParams } from "ag-grid-enterprise";
import { Button, Popover, Tag, Tooltip } from "antd";
import {
  MaterialLinksCellRenderer,
  TruncatedTextWithTooltip
} from "components/cell-renders";
import CustomTooltip from "components/custom-tooltip/custom-tooltip";
import FeatureLogSetStatusUI from "components/feature-status/feature-instance-status";
import {
  getUserFromSubmittalLogItem,
  transformDataFromSubmittalLogItem,
  updateSubmittalLogItemHistory
} from "components/microviz/microviz-data-transformations";
import MicrovizDateBlock from "components/microviz/microviz-dateblock";
import InfoIcon from "components/svg-icons/info-icon";
import UserTag from "components/user-tag/user-tag";
import TitleDescriptionTooltip from "components/widgets/title-description-tooltip";
import {
  AcceptanceStatus,
  ESubmittalStatus,
  SubmittalStatusToStrMap
} from "constants/index";
import { FeatureTypes } from "entity-app/constants";
import { getValueFromDotPath } from "entity-app/utils/utils";
import { Link } from "react-router-dom";
import ErrorBoundary from "components/error-boundary";
import SubmittalMicroVisualiser from "components/submittal-details/submittal-micro-viz";
import VoidSubmittalIcon from "components/svg-icons/void-submittal-icon";
import SubmittalMicroVizDateBlock from "components/submittal-details/submittal-microviz-dateblock";
import { getSubmittalDetailsLinkTo } from "pages/submittal-log/submittal-log-view";
import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";

export const linkCellRenderer = ({
  value,
  data,
  projectId,
  featureKey,
  context
}: any) => {
  const submittalId = data?.id || data?.feature_instance_id || "";
  const pathname = `/project/${projectId}/${featureKey}/${submittalId}`;
  const to =
    featureKey === "submittals" && context?.submittalLogReturnTo
      ? getSubmittalDetailsLinkTo(
          projectId,
          submittalId,
          context.submittalLogReturnTo
        )
      : { pathname };

  return (
    <Link className="gridIdLink" to={to}>
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
};

export const MaterialSequenceIdLinkCellRenderer = ({
  value,
  data,
  projectId,
  featureKey,
  context
}: any) => {
  const materialDetailLink = (
    <Link
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/${featureKey}/${
          data?.id || data?.feature_instance_id || ""
        }`
      }}
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
  const { ai_insights = {} } = data ?? {};

  const isAnyEntiries = Object.entries(ai_insights).length > 0;

  if (context?.isAiMaterialInsightsDashboardEnabled && isAnyEntiries) {
    const { setPdfSpecsInfo } = context;
    const spec = data.spec_section?.length
      ? data.spec_section[0]
      : {
          number: "",
          name: ""
        };

    return (
      <div
        className="flex items-center w-full group relative"
        style={{ minWidth: 0 }}
      >
        <div className="flex-1 min-w-[36px] overflow-hidden text-ellipsis whitespace-nowrap">
          {materialDetailLink}
        </div>
        <div className="ml-0.5 hidden group-hover:block">
          <Button
            size="small"
            onClick={() =>
              setPdfSpecsInfo({
                ai_insights,
                specSection: spec,
                title: data.title || ""
              })
            }
            className="text-[11px]"
          >
            View Specs
          </Button>
        </div>
      </div>
    );
  }
  return materialDetailLink;
};

export const userEntityCellRenderer = ({
  value,
  valueFormatted,
  statusId,
  companyObjField,
  data,
  context,
  node,
  column,
  colDef
}: any) => {
  const text = valueFormatted || value;
  if (typeof text !== "string") return "";
  const statusIdValue = getValueFromDotPath(data, statusId);
  const companyObj = getValueFromDotPath(data, companyObjField);
  const colId = colDef?.colId || colDef?.field;
  return (
    <div className="w-full flex items-center">
      <div className="overflow-hidden truncate grow flex-col leading-none space-y-1.5">
        <ErrorBoundary>
          <TruncatedTextWithTooltip
            text={text}
            tooltipContent={
              <TitleDescriptionTooltip
                title={text}
                description={companyObj?.name}
              />
            }
            dataTestId={colId}
          />
        </ErrorBoundary>

        {companyObj && (
          <div
            className="text-ciq-gray-800"
            data-testid={`${colId}_company-name`}
          >
            {companyObj?.name}
          </div>
        )}

        {statusIdValue === AcceptanceStatus.DEACTIVATED && (
          <div
            className="absolute flex w-full items-center justify-end pr-4 -top-1 pointer-events-none"
            data-testid="inactive-user-tag"
          >
            <UserTag label="Inactive" />
          </div>
        )}
      </div>
      <div>
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (context.onEditCell)
              context.onEditCell({
                rowIndex: node.rowIndex!,
                colKey: column!.getId(),
                data
              });
          }}
        />
      </div>
    </div>
  );
};

export const ArrayStringComparator = (a: any, b: any) => {
  return (a?.[0]?.name || "").localeCompare(b?.[0]?.name || "");
};

export const ArrayStringGetQuickFilterText = ({ value }: any) => {
  if (value && Array.isArray(value)) {
    return value.map((v) => v.name).join(" ");
  }
  return "";
};

export function ArrayStringCellRenderer(params: any) {
  const { value, featureKey, context, column, node, data } = params;

  const arr = (value || []) as Array<{ id: string; name: string }>;

  const toShow = arr.slice(0, 2);
  const remaining = arr.slice(2, arr.length);

  const popupContent = (
    <div className="max-w-[260px] m-0 text-xs flex flex-wrap gap-y-1 gap-x-0">
      {remaining.map((item: any) => {
        return (
          <Link
            key={`${item.id}${item.name}`}
            target="_blank"
            to={`${featureKey}/${item.id}`}
            onClick={(evt) => (featureKey ? null : evt.preventDefault())}
          >
            <Tag
              color="default"
              variant="outlined"
              icon={<LinkOutlined />}
              className="bg-gray-100 w-[120px] truncate mr-2"
            >
              <CustomTooltip value={item.name} />
            </Tag>
          </Link>
        );
      })}
    </div>
  );

  return (
    <div className="w-full flex items-center">
      <div className="grow overflow-hidden truncate flex items-center py-1 -pr-4 gap-y-1 gap-x-0">
        {toShow.map((item, index: number) => {
          return (
            <div className="flex items-center" key={`${item.id}${item.name}`}>
              <Link
                target="_blank"
                to={`${featureKey}/${item.id}`}
                onClick={(evt) => (featureKey ? null : evt.preventDefault())}
                className="leading-normal"
              >
                <Tag
                  color="default"
                  variant="outlined"
                  icon={<LinkOutlined />}
                  className="bg-gray-100 w-[120px] truncate mr-2"
                >
                  <CustomTooltip value={item.name} />
                </Tag>
              </Link>
              {index === 1 && arr.length > 2 ? (
                <div className="flex-none flex-grow-0 leading-none -m-1">
                  <Popover content={popupContent}>
                    <div>+{remaining.length}</div>
                  </Popover>
                </div>
              ) : (
                ""
              )}
            </div>
          );
        })}
      </div>
      <EditOutlined
        className="cell-edit-icon px-1"
        onClick={() => {
          if (context.onEditCell)
            context.onEditCell({
              rowIndex: node.rowIndex!,
              colKey: column!.getId(),
              data
            });
        }}
      />
    </div>
  );
}

export const twoRowGetQuickFilterText = ({ data }: any) => {
  try {
    return `${data.title}\n${data.description}`;
  } catch {
    return "";
  }
};

export const twoRowCellRenderer = (props: any) => {
  const { data, context, featureKey, projectId, colDef } = props;

  if (!data) return null;

  const colId = colDef?.colId || colDef?.field;

  return (
    <div className="w-full">
      <div className="!flex !items-center space-x-1">
        <Link
          className="gridIdLink grow truncate text-left"
          to={
            featureKey === "submittals" && context?.submittalLogReturnTo
              ? getSubmittalDetailsLinkTo(
                  projectId,
                  data.id,
                  context.submittalLogReturnTo
                )
              : {
                  pathname: `/project/${projectId}/${featureKey}/${data.id}`
                }
          }
        >
          <TruncatedTextWithTooltip text={data.title} dataTestId={colId} />
        </Link>
        <EditOutlined
          className="cell-edit-icon px-2"
          onClick={() => {
            context.onEditCell({
              rowIndex: props.node.rowIndex!,
              colKey: props.column!.getId(),
              data
            });
          }}
        />
      </div>
      <div className="colSecondValue">
        <TruncatedTextWithTooltip text={data.description} />
      </div>
    </div>
  );
};

/** Date on first line, time + timezone on second — same two-line layout as Title. */
export const dateTimeTwoLineCellRenderer = ({
  data,
  colDef,
  value
}: ICellRendererParams) => {
  const field = colDef?.field;
  const raw = (field && data?.[field]) || value;
  if (!raw) return "";

  const date = DateUtils.format(raw, DATE_FORMAT_MMDDYYYY);
  const time = DateUtils.format(raw, "hh:mm A (z)");
  if (!date) return "";

  return (
    <div className="w-full">
      <div className="colFirstValue">{date}</div>
      <div className="colSecondValue">{time}</div>
    </div>
  );
};

export const materialEarlyReleaseCellRenderer = ({ data, value }: any) => {
  const reason =
    data?.wf_override_reason || data?.material_released_early || "";
  if (reason) {
    return (
      <div className="flex items-center space-x-2">
        <span>Yes</span>
        <Tooltip title={`Reason - ${reason}`}>
          <span className="flex items-center">
            <InfoIcon />
          </span>
        </Tooltip>
      </div>
    );
  }
  // Materials leave the negative case blank; bid packages spell out "No".
  // Deferring to the column's own value keeps one renderer for both.
  return value ?? "";
};

export const featureStatusCellRenderer = (params: any) => {
  const { data, value, context } = params;

  const isEditable = context?.editPermission;

  const userDetails = (
    <div className="flex space-x-2 items-center justify-between">
      <div className="flex truncate">
        <TruncatedTextWithTooltip text={value} />
      </div>

      {isEditable && (
        <FeatureLogSetStatusUI
          data={data}
          featureType={context?.featureType}
          refetchLogData={context?.refetchLogData}
        />
      )}
    </div>
  );

  return userDetails;
};

export const submittalStatusCellRendererV2Report = (params: any) => {
  const { data, context } = params;
  const { isIntegrationMode } = context;

  if (data.workflow_status === ESubmittalStatus.VOID) {
    return <VoidSubmittalIcon />;
  }

  let dataForMicroviz: any = null;
  if (!isIntegrationMode) {
    const updatedHistory = updateSubmittalLogItemHistory(
      data,
      context.projectParticipants
    );

    dataForMicroviz = {
      submittal_history: updatedHistory,
      design_reviewer_id: data?.design_reviewer_id,
      gc_reviewer_id: data?.gc_reviewer_id,
      vendorInfo: {
        designReviewerCompanyName: null,
        responsibleContractorCompanyName: null
      }
    };
  }

  return (
    <div className="flex flex-col justify-start leading-none">
      <div className="leading-normal">
        {SubmittalStatusToStrMap[data.workflow_status]}
      </div>
      {isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVizDateBlock
              size="sm"
              submittalData={data}
              projectParticipants={context.projectParticipants}
              submittalRevisions={data.revision_date_blocks}
              selectedRevision={data.revision}
            />
          </ErrorBoundary>
        </div>
      )}

      {!isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVisualiser {...dataForMicroviz} size="sm" />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

export const submittalStatusCellRendererV2 = (params: any) => {
  const { data, context } = params;
  const { isIntegrationMode } = context;

  if (data.workflow_status.id === ESubmittalStatus.VOID) {
    return <VoidSubmittalIcon />;
  }

  let dataForMicroviz: any = null;
  if (!isIntegrationMode) {
    const updatedHistory = updateSubmittalLogItemHistory(
      data,
      context.projectParticipants
    );

    dataForMicroviz = {
      submittal_history: updatedHistory,
      design_reviewer_id: data?.design_reviewer?.id,
      gc_reviewer_id: data?.gc_user?.id,
      vendorInfo: {
        designReviewerCompanyName: data?.design_reviewer?.company?.name,
        responsibleContractorCompanyName: data?.responsible_contractor?.name
      }
    };
  }
  return (
    <div
      className="flex flex-col justify-start leading-none"
      data-testid="submittal-status-cell-renderer"
    >
      <div className="leading-normal">
        {SubmittalStatusToStrMap[data.workflow_status.id]}
      </div>
      {isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <MicrovizDateBlock
              submittalData={{ ...data, revision: 0, revision_date_blocks: [] }}
              submittalRevisions={[]}
              selectedRevision={0}
              dataTransformFunction={transformDataFromSubmittalLogItem}
              userGetterFunction={getUserFromSubmittalLogItem}
              size="sm"
            />
          </ErrorBoundary>
        </div>
      )}

      {!isIntegrationMode && (
        <div className="mt-1.5">
          <ErrorBoundary>
            <SubmittalMicroVisualiser {...dataForMicroviz} size="sm" />
          </ErrorBoundary>
        </div>
      )}
    </div>
  );
};

const formatReviewResponseItem = (item: any) => {
  return `${item.response || ""} ${item.username ? `(${item.username})` : ""}`;
};

export const reviewResponsesCellRenderer = (params: ICellRendererParams) => {
  const { value } = params;

  const items = (value || []) as Array<any>;
  if (!items?.length) return "";

  const formattedItems = items
    ?.map((item, idx) => ({
      key: `review-response-${idx}`,
      text: formatReviewResponseItem(item)
    }))
    ?.filter(({ text }) => text);
  // formatReviewResponseItem always returns a string with at least a space, so filter never empties a non-empty list (length guarded above)
  /* v8 ignore next */
  if (!formattedItems.length) return "";

  const firstItem = items[0].response;
  const remainingCount = formattedItems.length - 1;

  return (
    <Tooltip
      title={
        <div className="flex flex-col gap-1 text-xs text-ciq-gray-950">
          {formattedItems.map(({ key, text }) => (
            <div key={key}>{text}</div>
          ))}
        </div>
      }
    >
      <div className="cursor-default flex items-center -space-x-1">
        <Tag color="default" className="rounded-[4px]">
          {firstItem}
        </Tag>
        {remainingCount > 0 && (
          <span className="text-ciq-text-900 font-medium text-xs">
            +{remainingCount}
          </span>
        )}
      </div>
    </Tooltip>
  );
};

export function FeatureMaterialLinksCellRenderer(params: ICellRendererParams) {
  const { data, colDef } = params;
  const colId = colDef?.colId || colDef?.field;

  const rendererParams = {
    ...params,
    data: {
      materials: data?.linked_items
        ?.filter(
          (item: any) =>
            item.feature_type === FeatureTypes.MATERIALS && !item.implicit
        )
        .map((item: any) => ({
          ...item,
          material_sequence_id: item.sequence_id,

          spec_section_no: item.spec_section?.length
            ? item.spec_section[0].number
            : "",
          spec_section_name: item.spec_section?.length
            ? item.spec_section[0].name
            : ""
        }))
    }
  };

  return (
    <div className="leading-none" data-testid={`${colId}`}>
      {MaterialLinksCellRenderer(rendererParams)}
    </div>
  );
}

export const logGroupCellRenderer = (params: ICellRendererParams) => {
  const rowData = params.data;

  const firstChildData = params.node?.allLeafChildren?.[0]?.data;

  const data = rowData ?? firstChildData;
  if (!data) return <div>{params.value}</div>;

  const rowGroupColumns = (params as any)?.api?.getRowGroupColumns?.() ?? [];
  const level = params.node?.level ?? 0;
  const groupedColId =
    rowGroupColumns[level]?.getColId?.() ?? rowGroupColumns[level]?.colId;

  if (groupedColId === "assignee") {
    if (!data.assignee_user) {
      return (
        <div className="flex flex-col gap-1 pr-1">
          <TruncatedTextWithTooltip text="Unassigned" />
        </div>
      );
    }

    const assigneeName =
      `${data.assignee_user.first_name ?? ""} ${data.assignee_user.last_name ?? ""}`.trim() ||
      "Unassigned";

    return (
      <div className="leading-none flex flex-col gap-1 pt-1 pr-1">
        <TruncatedTextWithTooltip text={assigneeName} />
        {data.assignee_user.company?.name && (
          <div className="text-ciq-gray-800">
            <TruncatedTextWithTooltip text={data.assignee_user.company?.name} />
          </div>
        )}
      </div>
    );
  }

  if (groupedColId === "trade_partner") {
    const tradePartnerName = data.responsible_contractor
      ? data.responsible_contractor.name?.trim() || "Unassigned"
      : "Unassigned";

    return (
      <div className="flex flex-col gap-1 pr-1">
        <div>
          <TruncatedTextWithTooltip text={tradePartnerName} />
        </div>
      </div>
    );
  }

  if (groupedColId === "division") {
    if (!params.value) {
      return (
        <div className="flex gap-1 pr-1">
          <TruncatedTextWithTooltip text="No Division" />
        </div>
      );
    }
    return (
      <div className="flex gap-1 pr-1">
        {/* params.value is guaranteed truthy here; the !params.value case returned above, so the "" fallback is unreachable */}
        <TruncatedTextWithTooltip
          text={
            /* v8 ignore next */
            params.value || ""
          }
        />
      </div>
    );
  }

  // Fallback: render grouped value if we can't determine grouping column.
  return <div>{params.value}</div>;
};

/** Empty cell for group-header rows in normal data columns (not the auto group column). */
export function MaterialLogGroupRowPlaceholderCell() {
  return <div className="h-full w-full" aria-hidden />;
}
